class B2BSignupPage {
  constructor() {
    this.form = document.querySelector('.b2b-signup-form');
    if (!this.form) return;

    this.spinner = this.form.querySelector('.b2b-submit-wrap .spinner-border');
    this.submitBtn = this.form.querySelector('.b2b-submit-btn');
    this.successModal = document.getElementById('success_signup_modal');
    this.csrfTokenField = document.querySelector('#csrf_token');
    this.modalAutoCloseTimeout = null;

    // Form elements
    this.addressInput = this.form.querySelector('#address1');
    this.addressError = document.getElementById('addressError');
    this.facilityTypeSelect = this.form.querySelector('#facilityType');
    this.facilityTypeOtherGroup = document.getElementById('facilityTypeOtherGroup');
    this.facilityTypeOtherInput = document.getElementById('facilityTypeOther');
    this.facilityTypeOtherError = document.getElementById('facilityTypeOtherError');
    
    // City, Zip, Phone elements
    this.cityInput = this.form.querySelector('#city');
    this.cityError = document.getElementById('cityError');
    this.zipInput = this.form.querySelector('#zip');
    this.zipError = document.getElementById('zipError');
    this.phoneInput = this.form.querySelector('#contactPhone');
    this.phoneError = document.getElementById('phoneError');

    // Create message container
    this.formMessage = document.createElement('div');
    this.formMessage.className = 'form-message mt-3 text-center';
    this.form.appendChild(this.formMessage);

    this._generateCSRFToken();
    this._bindEvents();
    this._initFacilityTypeToggle();
  }

  _generateCSRFToken() {
    // Generate a shorter secure CSRF token with prefix
    const prefix = 'csrf_';
    let randomPart = '';

    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint8Array(8);
      // Fill the array with cryptographically secure random values
      window.crypto.getRandomValues(array);
      // Convert each byte to hexadecimal (2 hex chars per byte) and join them
      randomPart = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    } else {
      randomPart = Math.random().toString(36).substring(2, 14) + 
                   Date.now().toString(36).substring(0, 8) +
                   Math.random().toString(36).substring(2, 10);
    }
    
    const token = prefix + randomPart;
    
    // Store token in sessionStorage for validation
    if (typeof(Storage) !== 'undefined') {
      sessionStorage.setItem('b2b_csrf_token', token);
    }
    
    // Set token in hidden field
    if (this.csrfTokenField) {
      this.csrfTokenField.value = token;
    }
    
    return token;
  }

  _bindEvents() {
    // Success modal close button
    const successModalCloseBtn = this.successModal?.querySelector('[data-close-modal]');
    if (this.successModal && successModalCloseBtn) {
      successModalCloseBtn.addEventListener('click', () => {
        console.log('successModalCloseBtn clicked');
        if (this.modalAutoCloseTimeout) {
          clearTimeout(this.modalAutoCloseTimeout);
          this.modalAutoCloseTimeout = null;
        }
        this.successModal.hide();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    // Facility type toggle
    this.facilityTypeSelect?.addEventListener('change', this._toggleFacilityTypeOther.bind(this));

    // Phone number - only allow digits, max 10
    this.phoneInput?.addEventListener('input', (e) => {
      // Remove any non-digit characters
      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
    });

    // Form submit
    this.form.addEventListener('submit', this._onSubmit.bind(this));
  }

  _initFacilityTypeToggle() {
    this._toggleFacilityTypeOther();
  }

  _validateAddress(address) {
    const poBoxRegex = /\b[P|p]\.?[O|o]\.? Box\b/;
    const fakeRegex = /^(test|fake|asdf|qwerty|na|none)$/i;
    const validAddressRegex = /\d+\s+([A-Za-z]+)/;
    return !poBoxRegex.test(address) && !fakeRegex.test(address) && validAddressRegex.test(address);
  }

  _validateCity(city) {
    // City should contain only letters, spaces, hyphens, apostrophes, and periods
    // Minimum 2 characters, examples: "O'Fallon", "Winston-Salem", "St. Louis"
    const cityRegex = /^[A-Za-z][A-Za-z\s'\-\.]{1,}$/;
    const fakeRegex = /^(test|fake|asdf|qwerty|na|none|xxx)$/i;
    return cityRegex.test(city.trim()) && !fakeRegex.test(city.trim());
  }

  _validateZipCode(zip) {
    // US ZIP code: 5 digits or 5+4 format (12345 or 12345-6789)
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip.trim());
  }

  _validatePhone(phone) {
    // US phone: exactly 10 digits, no formatting
    // Must start with valid US area code (2-9 for first digit)
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length !== 10) return false;
    
    // First digit of area code must be 2-9 (not 0 or 1)
    // First digit of exchange code (4th digit) must be 2-9
    const areaCodeFirst = digitsOnly.charAt(0);
    const exchangeFirst = digitsOnly.charAt(3);
    
    if (areaCodeFirst === '0' || areaCodeFirst === '1') return false;
    if (exchangeFirst === '0' || exchangeFirst === '1') return false;
    
    return true;
  }

  _scrollToField(field) {
    const yOffset = -150; // adjust scroll offset if needed
    const y = field.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  _displayErrorMessages(errorData) {
    let errorMessage = '';
    let firstErrorField = null;

    // Reset all field errors
    const allInputs = this.form.querySelectorAll('.form-control');
    allInputs.forEach(input => {
      input.classList.remove('is-invalid');
    });
    const allErrorDivs = this.form.querySelectorAll('.invalid-feedback');
    allErrorDivs.forEach(div => div.classList.add('d-none'));

    // Display general error message
    if (errorData.error) {
      errorMessage = errorData.error;
    }

    // Display field-specific errors
    if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
      errorData.details.forEach(detail => {
        const fieldName = detail.field;
        const fieldMessage = detail.message;
        
        // Map API field names to form field IDs
        const fieldIdMap = {
          'contactPhone': 'contactPhone',
          'contactEmail': 'contactEmail',
          'contactFirstName': 'contactFirstName',
          'contactLastName': 'contactLastName',
          'contactRole': 'contactRole',
          'contactPrisonName': 'contactPrisonName',
          'address1': 'address1',
          'city': 'city',
          'zoneCode': 'zoneCode',
          'zip': 'zip',
          'facilityType': 'facilityType',
          'facilityTypeOther': 'facilityTypeOther',
          'participateInATChristmas': 'participateInATChristmas'
        };

        const fieldId = fieldIdMap[fieldName];
        if (fieldId) {
          const fieldElement = this.form.querySelector(`#${fieldId}`);
          if (fieldElement) {
            fieldElement.classList.add('is-invalid');
            
            // Try to find existing error div or create one
            let errorDiv = fieldElement.parentElement.querySelector('.invalid-feedback');
            if (!errorDiv) {
              errorDiv = document.createElement('div');
              errorDiv.className = 'invalid-feedback';
              fieldElement.parentElement.appendChild(errorDiv);
            }
            
            errorDiv.textContent = fieldMessage;
            errorDiv.classList.remove('d-none');
            
            // Track first error field for scrolling
            if (!firstErrorField) {
              firstErrorField = fieldElement;
            }
          }
        }

        // Add to general error message
        if (errorMessage) {
          errorMessage += '<br>';
        }
        errorMessage += fieldMessage;
      });
    }

    // Display error message in form message container
    this.formMessage.innerHTML = errorMessage || 'There was an error submitting the form. Please try again.';
    this.formMessage.classList.add('text-danger');

    // Scroll to first error field if available
    if (firstErrorField) {
      this._scrollToField(firstErrorField);
    }
  }

  _toggleFacilityTypeOther() {
    if (!this.facilityTypeSelect || !this.facilityTypeOtherGroup || !this.facilityTypeOtherInput) return;

    const isOtherSelected = this.facilityTypeSelect.value === 'other';
    if (isOtherSelected) {
      this.facilityTypeOtherGroup.classList.remove('d-none');
      this.facilityTypeOtherInput.required = true;
    } else {
      this.facilityTypeOtherGroup.classList.add('d-none');
      this.facilityTypeOtherInput.required = false;
      this.facilityTypeOtherInput.value = '';
      this.facilityTypeOtherInput.classList.remove('is-invalid');
      this.facilityTypeOtherError?.classList.add('d-none');
    }
  }

  _onSubmit(e) {
    e.preventDefault();

    const address = this.addressInput.value.trim();
    const city = this.cityInput?.value.trim() || '';
    const zip = this.zipInput?.value.trim() || '';
    const phone = this.phoneInput?.value.trim() || '';
    let isValid = true;
    let firstInvalidField = null;

    // Reset errors
    this.addressError?.classList.add('d-none');
    this.addressInput?.classList.remove('is-invalid');
    this.cityError?.classList.add('d-none');
    this.cityInput?.classList.remove('is-invalid');
    this.zipError?.classList.add('d-none');
    this.zipInput?.classList.remove('is-invalid');
    this.phoneError?.classList.add('d-none');
    this.phoneInput?.classList.remove('is-invalid');
    this.facilityTypeOtherInput?.classList.remove('is-invalid');
    this.facilityTypeOtherError?.classList.add('d-none');

    // Reset all field errors
    const allInputs = this.form.querySelectorAll('.form-control');
    allInputs.forEach(input => {
      input.classList.remove('is-invalid');
    });
    const allErrorDivs = this.form.querySelectorAll('.invalid-feedback');
    allErrorDivs.forEach(div => div.classList.add('d-none'));

    // Clear old form message
    this.formMessage.innerHTML = '';
    this.formMessage.classList.remove('text-success', 'text-danger');

    // Address validation
    if (!this._validateAddress(address)) {
      this.addressError?.classList.remove('d-none');
      this.addressInput?.classList.add('is-invalid');
      if (!firstInvalidField) firstInvalidField = this.addressInput;
      isValid = false;
    }

    // City validation
    if (!this._validateCity(city)) {
      this.cityError?.classList.remove('d-none');
      this.cityInput?.classList.add('is-invalid');
      if (!firstInvalidField) firstInvalidField = this.cityInput;
      isValid = false;
    }

    // ZIP code validation (US format)
    if (!this._validateZipCode(zip)) {
      this.zipError?.classList.remove('d-none');
      this.zipInput?.classList.add('is-invalid');
      if (!firstInvalidField) firstInvalidField = this.zipInput;
      isValid = false;
    }

    // Phone validation (10 digit US number)
    if (!this._validatePhone(phone)) {
      this.phoneError?.classList.remove('d-none');
      this.phoneInput?.classList.add('is-invalid');
      if (!firstInvalidField) firstInvalidField = this.phoneInput;
      isValid = false;
    }

    if (!isValid) {
      if (firstInvalidField) {
        this._scrollToField(firstInvalidField);
      }
      return;
    }

    // Show spinner + disable button
    this.spinner?.classList.replace('d-none', 'd-block');
    this.submitBtn.disabled = true;
    this.submitBtn.classList.add('disabled');

    if (this.modalAutoCloseTimeout) {
      clearTimeout(this.modalAutoCloseTimeout);
      this.modalAutoCloseTimeout = null;
    }

    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData);
    
    // Explicitly remove address2 key from data if it's empty or not provided
    if (!data.address2 || data.address2.trim() === '') {
      delete data.address2;
    }

    // Explicitly remove facilityTypeOther key from data if it's empty or not provided
    if (!data.facilityTypeOther || (typeof data.facilityTypeOther === 'string' && data.facilityTypeOther.trim() === '')) {
      delete data.facilityTypeOther;
    }
    
    Object.keys(data).forEach(key => {
      if (typeof data[key] === 'string') {
        data[key] = data[key].trim();
      }
      if (data[key] === '') delete data[key];
    });

    if (this.facilityTypeSelect?.value === 'other') {
      const otherValue = this.facilityTypeOtherInput?.value.trim();
      if (!otherValue) {
        this.facilityTypeOtherInput.classList.add('is-invalid');
        this.facilityTypeOtherError?.classList.remove('d-none');
        this._scrollToField(this.facilityTypeOtherInput);
        this.spinner?.classList.replace('d-block', 'd-none');
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('disabled');
        return;
      }
      data.facilityTypeOther = otherValue;
    } else if (this.facilityTypeOtherInput) {
      delete data.facilityTypeOther;
    }

    // Get CSRF token from hidden field or generate new one
    const csrfToken = this.csrfTokenField ? this.csrfTokenField.value : this._generateCSRFToken();
    
    // Include CSRF token in request body
    data.csrf_token = csrfToken;

    fetch(this.form.action, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken // Also send as header (common practice)
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      // Check if response status is 200 (success)
      if (response.status === 200) {
        return response.json().then(data => {
          this.form.reset();
          this._toggleFacilityTypeOther();

          // Open success modal instead of inline message
          if (this.successModal) {
            this.successModal.show(this.submitBtn);
            this.modalAutoCloseTimeout = window.setTimeout(() => {
              this.successModal.hide();
              window.scrollTo({ top: 0, behavior: 'smooth' });
              this.modalAutoCloseTimeout = null;
            }, 10000);
          } else {
            this.formMessage.textContent = 'Form submitted successfully!';
            this.formMessage.classList.add('text-success');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      } else {
        // If status is not 200, parse and display error messages
        return response.json().then(errorData => {
          this._displayErrorMessages(errorData);
        }).catch(() => {
          // If JSON parsing fails, show generic error
          this.formMessage.textContent = 'There was an error submitting the form. Please try again.';
          this.formMessage.classList.add('text-danger');
        });
      }
    })
    .catch(error => {
      console.error('Error:', error);

      this.formMessage.textContent = 'There was an error submitting the form. Please try again.';
      this.formMessage.classList.add('text-danger');
    })
    .finally(() => {
      this.spinner?.classList.replace('d-block', 'd-none');
      this.submitBtn.disabled = false;
      this.submitBtn.classList.remove('disabled');

      if (!this.formMessage.classList.contains('text-danger')) {
        this.formMessage.classList.remove('text-success');
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', function() {
  typeof B2BSignupPage !== 'undefined' && new B2BSignupPage();
});
